'use client'

import { productToAddFavoriteListDrawerAtom } from '@/feature/favorite/components/add-product-to-favorite-list-drawer/addProductToFavorite.store'
import { createFavoriteListAddProductDrawerOpenIntent } from '@/feature/favorite/services/favorite.intents'
import { useIsAuthenticated } from '@/lib/auth/client/AuthProvider.client'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import type { TrackingGaEvent } from '@/lib/tracking/services/tracking.eventTypes'
import { pushToGtmDataLayer } from '@/lib/tracking/services/tracking.gtm'
import { Button } from '@/ui/buttons/button/Button.client'
import { Icon } from '@/ui/icons/icon/Icon'
import { useSetAtom } from 'jotai'
import { useMemo } from 'react'

// AddProductToAddProductToFavoriteButton
// To be moves to feat
// stateful on click
export type AddProductToFavoriteButtonProps = {
  /** The reference of the correlated product */
  product: {
    reference: string
    name?: string
    isFavorite?: boolean
  }
  /** Context for tracking purposes */
  context?: 'default' | 'mono-discriminant' | 'multi-discriminant' | 'product-page'
}

export function AddProductToFavoriteButton({
  product,
  context = 'default',
}: AddProductToFavoriteButtonProps): React.ReactNode {
  const openFavoriteListDrawer = useSetAtom(productToAddFavoriteListDrawerAtom)
  const isAuthenticated = useIsAuthenticated()
  const { t } = useTranslation(['addProductToFavoriteButton'])

  const openAddProductToFavoriteListIntent = useMemo(
    () => createFavoriteListAddProductDrawerOpenIntent(product.reference, product.name, context),
    [context, product.name, product.reference],
  )

  if (!isAuthenticated) {
    return null
  }

  return (
    <Button
      size="sm"
      onPress={() => {
        let eventCategory = 'plot produit'
        let eventAction = 'wishlist'

        const trackingBase: TrackingGaEvent = {
          event: 'gaEvent',
          eventCategory,
          eventAction,
          eventLabel: 'clic coeur pour ouverture wishlist',
        }

        if (context === 'mono-discriminant') {
          eventCategory = 'fiche produit mono-discriminant'
          eventAction = 'wishlist - tableau de références'
        } else if (context === 'multi-discriminant') {
          eventCategory = 'fiche produit multi-discriminant'
          eventAction = 'wishlist - tableau de références'
        } else if (context === 'product-page') {
          eventCategory = 'fiche article / produit'
        }

        pushToGtmDataLayer({
          ...trackingBase,
          eventCategory,
          eventAction,
        })

        openFavoriteListDrawer({
          productSku: product.reference,
          productName: product.name ?? '',
          trackingEventCategory: eventCategory,
        })
      }}
      isRound
      iconLeft={<Icon.Heart />}
      variant={product.isFavorite ? 'primary' : 'tertiary'}
      intent={openAddProductToFavoriteListIntent}
    >
      {t('addProductToFavoriteButton:label')}
    </Button>
  )
}
